const TIER_TOTALS = { essentiel: 4, standard: 8, premium: 20 };
const TIER_MAX_EBOOKS = { essentiel: 0, standard: 3, premium: null };

class WriterPortalDashboard extends HTMLElement {
  constructor() {
    super();
    this.supabaseUrl = this.dataset.supabaseUrl;
    this.anonKey = this.dataset.supabaseAnonKey;
    this.loginUrl = this.dataset.loginUrl || '/';
    this.locale = this.dataset.locale === 'ar' ? 'ar' : 'en';
    this.dateLocale = this.locale === 'ar' ? 'ar-DZ' : 'en-US';
    this.monthFormatter = new Intl.DateTimeFormat(this.dateLocale, { year: 'numeric', month: 'long' });

    const i18nScript = this.querySelector('.writer-portal-dashboard__i18n');
    this.i18n = i18nScript ? JSON.parse(i18nScript.textContent) : {};

    this.signedOutEl = this.querySelector('.writer-portal-dashboard__signed-out');
    this.panel = this.querySelector('.writer-portal-dashboard__panel');
    this.tabButtons = this.querySelectorAll('.writer-portal-dashboard__tab-btn');
    this.tabPanels = this.querySelectorAll('.writer-portal-dashboard__tab-panel');
    this.logoutBtn = this.querySelector('.writer-portal-dashboard__logout');

    this.accountEl = this.querySelector('.writer-portal-dashboard__account');
    this.accountBtn = this.querySelector('.writer-portal-dashboard__account-btn');
    this.accountMenu = this.querySelector('.writer-portal-dashboard__account-menu');
    this.overviewEl = this.querySelector('.writer-portal-dashboard__overview');

    this.statGrid = this.querySelector('.writer-portal-dashboard__stat-grid');

    this.quotaEl = this.querySelector('.writer-portal-dashboard__quota');
    this.addBookForm = this.querySelector('.writer-portal-dashboard__add-book-form');
    this.formatSelect = this.addBookForm.querySelector('[name="format"]');
    this.stockRow = this.querySelector('.writer-portal-dashboard__stock-row');
    this.fileInput = this.addBookForm.querySelector('[name="file"]');
    this.filePrintHint = this.querySelector('.writer-portal-dashboard__file-print-hint');
    this.isPackCheckbox = this.addBookForm.querySelector('[name="is_pack"]');
    this.coverPackHint = this.querySelector('.writer-portal-dashboard__cover-pack-hint');
    this.bookMessage = this.querySelector('.writer-portal-dashboard__book-message');
    this.booksGrid = this.querySelector('.writer-portal-dashboard__books-grid');
    this.booksEmpty = this.querySelector('.writer-portal-dashboard__books-empty');

    this.salesSummaryEl = this.querySelector('.writer-portal-dashboard__sales-summary');
    this.salesBody = this.querySelector('.writer-portal-dashboard__sales-body');
    this.salesEmpty = this.querySelector('.writer-portal-dashboard__sales-empty');
    this.historyHead = this.querySelector('.writer-portal-dashboard__history-head');
    this.historyBody = this.querySelector('.writer-portal-dashboard__history-body');

    this.accessToken = sessionStorage.getItem('loubna_writer_token');
    this.refreshToken = sessionStorage.getItem('loubna_writer_refresh_token');
    this.tokenExpiresAt = Number(sessionStorage.getItem('loubna_writer_token_expires_at') || 0);
    this.refreshTimer = null;
    this.subscription = null;
    this.books = [];
    this.salesData = null;

    this.tabButtons.forEach((btn) => btn.addEventListener('click', () => this.switchTab(btn.dataset.tab)));
    this.logoutBtn.addEventListener('click', this.handleLogout.bind(this));
    this.addBookForm.addEventListener('submit', this.handleAddBook.bind(this));
    this.formatSelect.addEventListener('change', this.updateStockRowVisibility.bind(this));
    this.formatSelect.addEventListener('change', this.updateFileFieldState.bind(this));
    if (this.isPackCheckbox) {
      this.isPackCheckbox.addEventListener('change', this.updateCoverHintVisibility.bind(this));
    }
    this.accountBtn.addEventListener('click', this.toggleAccountMenu.bind(this));
    document.addEventListener('click', (event) => {
      if (!this.accountEl.contains(event.target)) this.closeAccountMenu();
    });
    this.updateStockRowVisibility();
    this.updateFileFieldState();
    this.updateCoverHintVisibility();

    const hashSession = this.getSessionFromHash();
    if (hashSession) {
      this.storeSession(hashSession);
      history.replaceState(null, '', window.location.pathname + window.location.search);
      this.showDashboard();
    } else if (this.accessToken) {
      if (this.tokenExpiresAt && this.tokenExpiresAt <= Date.now()) {
        // The access token already expired while the tab was closed/idle -
        // try a silent refresh before falling back to the signed-out view.
        this.refreshSession().then((ok) => {
          if (ok) this.showDashboard();
        });
      } else {
        this.scheduleTokenRefresh();
        this.showDashboard();
      }
    }
  }

  t(key) {
    return this.i18n[key] || key;
  }

  tierLabel(tier) {
    return this.t(`tier_${tier}`);
  }

  formatLabel(format) {
    return format === 'ebook' ? this.t('format_ebook') : this.t('format_print');
  }

  statusLabel(status) {
    return this.t(`status_${status}`);
  }

  toggleAccountMenu() {
    const isOpen = !this.accountMenu.hidden;
    this.accountMenu.hidden = isOpen;
    this.accountEl.classList.toggle('is-open', !isOpen);
  }

  closeAccountMenu() {
    this.accountMenu.hidden = true;
    this.accountEl.classList.remove('is-open');
  }

  updateStockRowVisibility() {
    this.stockRow.hidden = this.formatSelect.value !== 'print';
  }

  updateFileFieldState() {
    // Print books don't need a manuscript file - the writer already supplies
    // physical copies directly (see stock_quantity), nothing for the store
    // to print from. Only ebooks need this field, since the PDF *is* the
    // product. Clearing the value on switch to print also stops a
    // leftover file from a prior "ebook" selection silently riding along.
    const isPrint = this.formatSelect.value === 'print';
    this.fileInput.disabled = isPrint;
    if (isPrint) this.fileInput.value = '';
    if (this.filePrintHint) this.filePrintHint.hidden = !isPrint;
  }

  updateCoverHintVisibility() {
    if (this.coverPackHint) this.coverPackHint.hidden = !this.isPackCheckbox.checked;
  }

  getSessionFromHash() {
    const hash = new URLSearchParams(window.location.hash.replace('#', ''));
    const accessToken = hash.get('access_token');
    if (!accessToken) return null;
    return {
      access_token: accessToken,
      refresh_token: hash.get('refresh_token'),
      expires_in: hash.get('expires_in'),
    };
  }

  // Access tokens are short-lived (30 min) on purpose - so a stolen token
  // is only useful for a short window - but that means a real work session
  // needs to renew itself in the background via the refresh token Supabase
  // already issues alongside it, or the writer would get signed out
  // mid-task every 30 minutes. Called after both a fresh sign-in and a
  // successful refresh, so the next renewal is always scheduled.
  storeSession(data) {
    this.accessToken = data.access_token;
    this.refreshToken = data.refresh_token;
    this.tokenExpiresAt = Date.now() + (Number(data.expires_in) || 1800) * 1000;
    sessionStorage.setItem('loubna_writer_token', this.accessToken);
    sessionStorage.setItem('loubna_writer_refresh_token', this.refreshToken);
    sessionStorage.setItem('loubna_writer_token_expires_at', String(this.tokenExpiresAt));
    this.scheduleTokenRefresh();
  }

  scheduleTokenRefresh() {
    if (this.refreshTimer) clearTimeout(this.refreshTimer);
    // Refresh 2 minutes before the access token actually expires, so
    // there's always a valid token ready before any request could hit a
    // stale one - never less than 5s out, in case the clock skew or a
    // slow page load already ate into that buffer.
    const msUntilRefresh = Math.max(this.tokenExpiresAt - Date.now() - 120000, 5000);
    this.refreshTimer = setTimeout(() => this.refreshSession(), msUntilRefresh);
  }

  async refreshSession() {
    if (!this.refreshToken) {
      this.handleLogout();
      return false;
    }
    try {
      const response = await fetch(`${this.supabaseUrl}/auth/v1/token?grant_type=refresh_token`, {
        method: 'POST',
        headers: { apikey: this.anonKey, 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: this.refreshToken }),
      });
      if (!response.ok) throw new Error('refresh_failed');
      const data = await response.json();
      this.storeSession(data);
      return true;
    } catch (error) {
      // The refresh token itself is invalid/expired/revoked - nothing left
      // to try, back to the signed-out view.
      this.handleLogout();
      return false;
    }
  }

  handleLogout() {
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }
    sessionStorage.removeItem('loubna_writer_token');
    sessionStorage.removeItem('loubna_writer_refresh_token');
    sessionStorage.removeItem('loubna_writer_token_expires_at');
    this.accessToken = null;
    this.refreshToken = null;
    this.tokenExpiresAt = 0;
    this.panel.hidden = true;
    this.signedOutEl.hidden = false;
  }

  switchTab(tab) {
    this.tabButtons.forEach((btn) => btn.classList.toggle('is-active', btn.dataset.tab === tab));
    this.tabPanels.forEach((panel) => {
      panel.hidden = panel.dataset.tabPanel !== tab;
    });
  }

  async showDashboard() {
    this.signedOutEl.hidden = true;
    this.panel.hidden = false;
    await Promise.all([this.loadSubscription(), this.loadBooks(), this.loadSales()]);
  }

  async loadSubscription() {
    try {
      const response = await fetch(
        `${this.supabaseUrl}/rest/v1/writer_submissions?status=eq.active&select=*&order=activated_at.desc&limit=1`,
        { headers: { apikey: this.anonKey, Authorization: `Bearer ${this.accessToken}` } }
      );
      if (response.status === 401) {
        this.handleLogout();
        return;
      }
      const rows = response.ok ? await response.json() : [];
      this.subscription = rows[0] || null;
      this.renderOverview();
      this.renderQuota();
      this.renderStats();
    } catch (error) {
      this.subscription = null;
      this.renderOverview();
    }
  }

  renderOverview() {
    if (!this.subscription) {
      this.overviewEl.innerHTML = `<p>${escapeHtml(this.t('no_active_subscription'))}</p>`;
      return;
    }
    const tierLabel = this.tierLabel(this.subscription.tier) || escapeHtml(this.subscription.tier || '');
    const priceUnit = this.subscription.region === 'intl' ? '$' : 'DA';
    const renewsAt = this.subscription.renews_at
      ? new Date(this.subscription.renews_at).toLocaleDateString(this.dateLocale)
      : '—';
    const refundNote =
      this.subscription.refund_eligible_until && new Date(this.subscription.refund_eligible_until) > new Date()
        ? `<p class="writer-portal-dashboard__refund-note">${escapeHtml(this.t('overview_refund_note'))} ${new Date(
            this.subscription.refund_eligible_until
          ).toLocaleDateString(this.dateLocale)}</p>`
        : '';

    this.overviewEl.innerHTML = `
      <div class="writer-portal-dashboard__overview-card">
        <p><strong>${escapeHtml(this.t('overview_plan'))}</strong> ${tierLabel}</p>
        <p><strong>${escapeHtml(this.t('overview_price'))}</strong> ${this.subscription.subscription_price != null ? this.subscription.subscription_price + ' ' + priceUnit : '—'}</p>
        <p><strong>${escapeHtml(this.t('overview_renews_at'))}</strong> ${renewsAt}</p>
        ${refundNote}
      </div>`;
  }

  renderQuota() {
    if (!this.subscription) {
      this.quotaEl.innerHTML = '';
      return;
    }
    const total = TIER_TOTALS[this.subscription.tier];
    const maxEbooks = TIER_MAX_EBOOKS[this.subscription.tier];
    if (total === undefined) {
      this.quotaEl.innerHTML = '';
      return;
    }
    const activeBooks = this.books.filter((b) => b.status !== 'rejected');
    const ebookCount = activeBooks.filter((b) => b.format === 'ebook').length;
    let text = `${this.t('quota_used')} ${activeBooks.length} / ${total}`;
    if (maxEbooks === 0) {
      text += ` ${this.t('quota_ebook_restricted')}`;
    } else if (maxEbooks !== null) {
      text += ` (${ebookCount} / ${maxEbooks} ${this.t('quota_ebook_max')})`;
    }
    this.quotaEl.innerHTML = `<p class="writer-portal-dashboard__quota-text">${escapeHtml(text)}</p>`;
  }

  renderStats() {
    const total = this.subscription ? TIER_TOTALS[this.subscription.tier] : undefined;
    const activeBooks = this.books.filter((b) => b.status !== 'rejected');
    const publishedCount = this.books.filter((b) => b.status === 'published').length;
    const unitsThisMonth = (this.salesData?.books || []).reduce((sum, b) => sum + b.units_sold, 0);

    const cards = [
      {
        label: this.t('stat_current_plan'),
        value: (this.subscription && this.tierLabel(this.subscription.tier)) || '—',
        sub: this.subscription
          ? `${this.subscription.subscription_price ?? '—'} ${this.subscription.region === 'intl' ? '$' : 'DA'} ${this.t('stat_per_month')}`
          : this.t('stat_no_active_subscription'),
      },
      {
        label: this.t('stat_earnings_this_month'),
        value: this.salesData ? `${this.salesData.total_net_earnings} ${this.locale === 'ar' ? 'دج' : 'DA'}` : '—',
        sub: this.salesData
          ? `${this.t('stat_after_commission')} ${this.salesData.commission_percent}% ${this.t('stat_after_commission_suffix')}`.trim()
          : '',
      },
      {
        label: this.t('stat_published_books'),
        value: `${publishedCount} / ${total !== undefined ? total : '—'}`,
        sub: `${activeBooks.length} ${this.t('stat_books_registered')}`,
      },
      {
        label: this.t('stat_sales_this_month'),
        value: `${unitsThisMonth}`,
        sub: this.t('stat_units_sold'),
      },
    ];

    this.statGrid.innerHTML = cards
      .map(
        (c) => `
        <div class="writer-portal-dashboard__stat-card">
          <p class="writer-portal-dashboard__stat-label">${escapeHtml(c.label)}</p>
          <p class="writer-portal-dashboard__stat-value">${c.value}</p>
          <p class="writer-portal-dashboard__stat-sub">${escapeHtml(c.sub)}</p>
        </div>`
      )
      .join('');
  }

  async loadBooks() {
    try {
      const response = await fetch(`${this.supabaseUrl}/rest/v1/writer_books?select=*&order=created_at.desc`, {
        headers: { apikey: this.anonKey, Authorization: `Bearer ${this.accessToken}` },
      });
      this.books = response.ok ? await response.json() : [];
      this.renderBooks();
      this.renderQuota();
      this.renderStats();
    } catch (error) {
      this.books = [];
      this.renderBooks();
    }
  }

  renderBooks() {
    this.booksEmpty.hidden = this.books.length > 0;
    const liveById = new Map((this.salesData?.books || []).map((b) => [b.book_id, b]));

    this.booksGrid.innerHTML = this.books
      .map((b) => {
        const live = liveById.get(b.id);
        const price = live ? live.price : b.price;
        const stockText =
          b.format !== 'print'
            ? this.t('book_stock_unlimited')
            : live && live.stock_quantity != null
            ? live.stock_quantity
            : (b.stock_quantity ?? '—');

        const coverCell =
          live && live.image_url
            ? `<img src="${escapeHtml(live.image_url)}" alt="" class="writer-portal-dashboard__book-cover">`
            : `<div class="writer-portal-dashboard__book-cover-placeholder">${escapeHtml(b.book_title)}</div>`;

        const linkCell =
          live && live.product_path
            ? `<a href="${escapeHtml(live.product_path)}" target="_blank" rel="noopener" class="writer-portal-dashboard__book-link">${escapeHtml(this.t('book_view_in_store'))}</a>`
            : '';

        const editCell =
          b.status === 'published'
            ? `
            <div class="writer-portal-dashboard__edit-row" data-book-id="${b.id}">
              <input type="number" min="0" step="0.01" class="writer-portal-dashboard__edit-price" placeholder="${escapeHtml(this.t('book_price_placeholder'))}" value="${price ?? ''}">
              ${
                b.format === 'print'
                  ? `<input type="number" min="0" step="1" class="writer-portal-dashboard__edit-stock" placeholder="${escapeHtml(this.t('book_stock_placeholder'))}" value="${stockText === '—' ? '' : stockText}">`
                  : ''
              }
              <button type="button" class="writer-portal-dashboard__link" data-save-book="${b.id}">${escapeHtml(this.t('book_save'))}</button>
              <span class="writer-portal-dashboard__edit-error" hidden></span>
            </div>`
            : '';

        return `
        <div class="writer-portal-dashboard__book-card">
          ${coverCell}
          <div class="writer-portal-dashboard__book-body">
            <p class="writer-portal-dashboard__book-title">${escapeHtml(b.book_title)}</p>
            <div class="writer-portal-dashboard__book-meta">
              <span class="writer-portal-dashboard__badge">${escapeHtml(this.formatLabel(b.format))}</span>
              ${b.is_pack ? `<span class="writer-portal-dashboard__badge writer-portal-dashboard__badge--pack">${escapeHtml(this.t('badge_pack'))}</span>` : ''}
              <span class="writer-portal-dashboard__badge writer-portal-dashboard__badge--status-${b.status}">${escapeHtml(this.statusLabel(b.status))}</span>
            </div>
            <div class="writer-portal-dashboard__book-meta">
              <span>${price != null ? price + ' ' + (this.locale === 'ar' ? 'دج' : 'DA') : '—'}</span>
              <span>${escapeHtml(this.t('book_stock_label'))} ${stockText}</span>
            </div>
            ${linkCell}
            ${editCell}
          </div>
        </div>`;
      })
      .join('');

    this.booksGrid.querySelectorAll('[data-save-book]').forEach((btn) => {
      btn.addEventListener('click', () => this.handleUpdateBook(btn.dataset.saveBook, btn));
    });
  }

  async handleUpdateBook(bookId, triggerBtn) {
    const row = triggerBtn.closest('.writer-portal-dashboard__edit-row');
    const priceInput = row.querySelector('.writer-portal-dashboard__edit-price');
    const stockInput = row.querySelector('.writer-portal-dashboard__edit-stock');
    const errorEl = row.querySelector('.writer-portal-dashboard__edit-error');
    if (errorEl) errorEl.hidden = true;

    const payload = { book_id: bookId };
    if (priceInput && priceInput.value) payload.price = Number(priceInput.value);
    if (stockInput && stockInput.value) payload.stock_quantity = Number(stockInput.value);
    if (payload.price === undefined && payload.stock_quantity === undefined) return;

    triggerBtn.disabled = true;
    try {
      const response = await fetch(`${this.supabaseUrl}/functions/v1/writer-update-book`, {
        method: 'POST',
        headers: {
          apikey: this.anonKey,
          Authorization: `Bearer ${this.accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        if (errorEl) {
          errorEl.textContent = this.t(`error_${data.message}`) !== `error_${data.message}` ? this.t(`error_${data.message}`) : this.t('error_generic');
          errorEl.hidden = false;
        }
        triggerBtn.disabled = false;
        return;
      }
      await Promise.all([this.loadBooks(), this.loadSales()]);
    } catch (error) {
      if (errorEl) {
        errorEl.textContent = this.t('error_generic');
        errorEl.hidden = false;
      }
      triggerBtn.disabled = false;
    }
  }

  async handleAddBook(event) {
    event.preventDefault();
    this.bookMessage.style.display = 'none';

    const formData = new FormData(this.addBookForm);
    const bookTitle = String(formData.get('book_title') || '').trim();
    const format = formData.get('format');
    const price = Number(formData.get('price'));
    const stockQuantity = format === 'print' ? Number(formData.get('stock_quantity')) : undefined;
    const genre = String(formData.get('genre') || '').trim();
    const description = String(formData.get('description') || '').trim();
    const file = formData.get('file');
    const isPack = formData.get('is_pack') === 'on';
    const coverInput = this.addBookForm.querySelector('[name="cover"]');
    const coverFiles = coverInput && coverInput.files ? [...coverInput.files].filter((f) => f.size) : [];

    if (!bookTitle || !(price > 0)) return;

    const submitBtn = this.addBookForm.querySelector('button[type="submit"]');
    submitBtn.disabled = true;

    try {
      const payload = {
        book_title: bookTitle,
        format,
        price,
        stock_quantity: stockQuantity,
        genre: genre || undefined,
        description: description || undefined,
        is_pack: isPack,
      };
      if (file instanceof File && file.size) {
        payload.file_base64 = await fileToBase64(file);
        payload.file_name = file.name;
        payload.content_type = file.type || 'application/pdf';
      }
      if (coverFiles.length) {
        // First image stays in the single cover_* fields the API already
        // accepted before packs existed - additional images (packs only)
        // go in `covers`, uploaded as extra Shopify product images.
        payload.cover_base64 = await fileToBase64(coverFiles[0]);
        payload.cover_file_name = coverFiles[0].name;
        payload.cover_content_type = coverFiles[0].type || 'image/jpeg';
        if (coverFiles.length > 1) {
          payload.additional_covers = await Promise.all(
            coverFiles.slice(1).map(async (f) => ({
              base64: await fileToBase64(f),
              file_name: f.name,
              content_type: f.type || 'image/jpeg',
            }))
          );
        }
      }

      const response = await fetch(`${this.supabaseUrl}/functions/v1/writer-submit-book`, {
        method: 'POST',
        headers: {
          apikey: this.anonKey,
          Authorization: `Bearer ${this.accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        this.showBookMessage(false, this.t(`error_${data.message}`) !== `error_${data.message}` ? this.t(`error_${data.message}`) : this.t('book_submit_error'));
        return;
      }

      this.addBookForm.reset();
      this.updateStockRowVisibility();
      this.updateFileFieldState();
      this.updateCoverHintVisibility();
      this.showBookMessage(true, this.t('book_submit_success'));
      await this.loadBooks();
    } catch (error) {
      this.showBookMessage(false, this.t('book_submit_error'));
    } finally {
      submitBtn.disabled = false;
    }
  }

  showBookMessage(success, text) {
    this.bookMessage.textContent = text;
    this.bookMessage.style.color = success ? '#173e33' : '#b3261e';
    this.bookMessage.style.display = 'block';
  }

  async loadSales() {
    try {
      const response = await fetch(`${this.supabaseUrl}/functions/v1/get-writer-sales`, {
        headers: { apikey: this.anonKey, Authorization: `Bearer ${this.accessToken}` },
      });
      if (!response.ok) throw new Error('sales_failed');
      const data = await response.json();
      this.salesData = data;
      this.renderSales(data);
      this.renderHistory(data);
      this.renderBooks();
      this.renderStats();
    } catch (error) {
      this.salesSummaryEl.innerHTML = '';
      this.salesEmpty.hidden = false;
    }
  }

  renderSales(data) {
    const books = data.books || [];
    const currency = this.locale === 'ar' ? 'دج' : 'DA';
    this.salesEmpty.hidden = books.some((b) => b.units_sold > 0);
    this.salesSummaryEl.innerHTML = `
      <p class="writer-portal-dashboard__sales-total">
        ${escapeHtml(this.t('sales_total'))} <strong>${data.total_net_earnings || 0}</strong> ${currency}
        (${escapeHtml(this.t('sales_after_commission'))} ${data.commission_percent}% ${escapeHtml(this.t('sales_store_commission'))})
      </p>`;
    this.salesBody.innerHTML = books
      .map(
        (b) => `
        <tr>
          <td>${escapeHtml(b.book_title)}</td>
          <td>${escapeHtml(this.formatLabel(b.format))}</td>
          <td>${b.units_sold}</td>
          <td>${b.net_earnings}</td>
        </tr>`
      )
      .join('');
  }

  renderHistory(data) {
    const months = (data.monthly_totals || []).map((m) => m.month);
    this.historyHead.innerHTML =
      `<th>${escapeHtml(this.t('table_book'))}</th>` +
      months.map((m) => `<th>${escapeHtml(this.formatMonth(m))}</th>`).join('');

    this.historyBody.innerHTML = (data.books || [])
      .map((b) => {
        const cells = b.history.map((h) => `<td>${h.units_sold} (${h.net_earnings})</td>`).join('');
        return `<tr><td>${escapeHtml(b.book_title)}</td>${cells}</tr>`;
      })
      .join('');
  }

  formatMonth(monthKey) {
    const [year, month] = monthKey.split('-').map(Number);
    return this.monthFormatter.format(new Date(Date.UTC(year, month - 1, 1)));
  }
}

function escapeHtml(value) {
  const div = document.createElement('div');
  div.textContent = value ?? '';
  return div.innerHTML;
}

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(',')[1]);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

customElements.define('writer-portal-dashboard', WriterPortalDashboard);
